// src/pages/ForgotPasswordPage.tsx
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, TextField, Typography, Paper } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { authAPI } from '../services/api';
import { SignInButton } from '../components/common/Buttons';
import { colors } from '../theme/theme';
import { InlineMessage } from '../components/common/InlineMessage';

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor:
    theme.palette.mode === 'dark' ? colors.grey[800] : colors.grey[300],
  paddingBottom: '120px',

  '@media (max-width: 768px)': {
    paddingBottom: '8rem',
  },
}));

const PagePaper = styled(Paper)({
  padding: '1rem',
  width: '100%',
  maxWidth: '400px',
  borderRadius: '12px',
  border: `2px solid ${colors.primary.main}`,
  boxShadow: '0 8px 14px rgba(0,0,0,0.25)',

  '@media (max-width: 768px)': {
    padding: '1rem 1rem',
    maxWidth: '90%',
    borderRadius: '12px',
  },
});

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': { borderRadius: '2px' },
  },
  '@media (max-width: 768px)': {
    marginTop: '1rem',
    marginBottom: '1rem',
  },
};

const backLinkStyle = {
  color: colors.primary.main,
  textDecoration: 'none',
  fontSize: '1.4rem',
};

const ForgotPasswordPage: React.FC = () => {
  const theme = useTheme();
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!usernameOrEmail.trim()) {
      setError('Please enter your username or email.');
      return;
    }

    setIsLoading(true);
    try {
      await authAPI.forgotPassword(usernameOrEmail.trim());
      setSubmitted(true);
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setError(
          'Too many reset requests. Please wait a few minutes and try again.'
        );
      } else if (err instanceof TypeError || err?.name === 'TypeError') {
        setError(
          'Unable to reach the server. Please check your connection and try again.'
        );
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <PagePaper elevation={3}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              color:
                theme.palette.mode === 'dark'
                  ? theme.palette.primary.light
                  : 'inherit',
            }}
          >
            Reset your password
          </Typography>
        </Box>

        {submitted ? (
          <>
            <InlineMessage
              message="If an account with that username or email exists, we've sent a password reset link."
              severity="success"
            />
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <RouterLink to="/login" style={backLinkStyle}>
                Back to login
              </RouterLink>
            </Box>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <Typography
              color="text.secondary"
              sx={{ mb: 1, fontSize: '1.5rem' }}
            >
              Enter your username or email and we'll send you a link to reset
              your password.
            </Typography>

            {error && <InlineMessage message={error} severity="error" />}

            <TextField
              fullWidth
              label="Username or Email"
              variant="outlined"
              margin="normal"
              value={usernameOrEmail}
              onChange={(e) => setUsernameOrEmail(e.target.value)}
              disabled={isLoading}
              autoFocus
              sx={fieldSx}
            />
            <SignInButton
              text={isLoading ? 'Sending...' : 'Send Reset Link'}
              isLoading={isLoading}
              disabled={isLoading}
            />
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <RouterLink to="/login" style={backLinkStyle}>
                Back to login
              </RouterLink>
            </Box>
          </form>
        )}
      </PagePaper>
    </PageContainer>
  );
};

export default ForgotPasswordPage;
